import { LISTINGS_JSON_PATH } from '#shared/constants';
import type {
  ListingSubmission,
  ListingsSnapshot,
  ListingRecord,
} from '#shared/types';
import {
  dummyApproveListing,
  dummyCreateListing,
  dummyDeclineListing,
  dummyFetchAdminListings,
  dummyPresignUpload,
  dummyUnpublishListing,
  dummyUpdateListing,
} from '#src/api/dummyAdminStore';
import { DUMMY_LISTINGS_SNAPSHOT } from '#src/api/dummyListings';
import { loadRuntimeConfig } from '#src/config';
import { hasLocalAdminSession } from '#src/utils/localAdmin';
import { isLocalDemoMode } from '#src/utils/localDemo';

const getApiUrl = async (): Promise<string> => {
  const config = await loadRuntimeConfig();
  return config.apiUrl;
};

export const fetchListings = async (): Promise<ListingsSnapshot> => {
  if (process.env.NODE_ENV === 'development') {
    return DUMMY_LISTINGS_SNAPSHOT;
  }

  const response = await fetch(LISTINGS_JSON_PATH);
  if (!response.ok) {
    throw new Error('Failed to load listings');
  }
  return response.json() as Promise<ListingsSnapshot>;
};

export const submitListing = async (
  submission: ListingSubmission,
): Promise<{ id: string }> => {
  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/submissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(submission),
  });

  const data = await response.json();
  if (!response.ok) {
    throw data;
  }
  return data as { id: string };
};

export const presignUpload = async (
  contentType: string,
): Promise<{ url: string; fields: Record<string, string>; key: string }> => {
  // Only stub uploads for the local admin UI; public submissions still hit the API.
  if (isLocalDemoMode() && hasLocalAdminSession()) {
    return dummyPresignUpload(contentType);
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/uploads/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contentType }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error ?? 'Failed to get upload URL');
  }
  return data;
};

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export const fetchAdminListings = async (
  token: string,
  status: string,
): Promise<ListingRecord[]> => {
  if (isLocalDemoMode()) {
    return dummyFetchAdminListings(status);
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(
    `${apiUrl}/admin/listings?status=${encodeURIComponent(status)}`,
    { headers: authHeaders(token) },
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error ?? 'Failed to load listings');
  }
  return (data as { listings: ListingRecord[] }).listings;
};

export const approveListing = async (
  token: string,
  id: string,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyApproveListing(id);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/listings/${id}/approve`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error ?? 'Failed to approve');
  }
};

export const declineListing = async (
  token: string,
  id: string,
  moderatorNote?: string,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyDeclineListing(id, moderatorNote);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/listings/${id}/decline`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ moderatorNote }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error ?? 'Failed to decline');
  }
};

export const unpublishListing = async (
  token: string,
  id: string,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyUnpublishListing(id);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/listings/${id}/unpublish`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error ?? 'Failed to unpublish');
  }
};

export const updateListing = async (
  token: string,
  id: string,
  updates: Partial<ListingRecord>,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyUpdateListing(id, updates);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/listings/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error ?? 'Failed to update');
  }
};

export const createListing = async (
  token: string,
  submission: ListingSubmission,
): Promise<ListingRecord> => {
  if (isLocalDemoMode()) {
    return dummyCreateListing(submission);
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/listings`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(submission),
  });
  const data = await response.json();
  if (!response.ok) {
    throw data;
  }
  return (data as { listing: ListingRecord }).listing;
};
